import {
    Alert2,
    ArrowTurnForward,
    Cancel,
    CheckMark,
    Download,
    Eye,
    Notification,
    Play,
} from "@/assets/icons";
import { ButtonStateEnum } from "@/enums";
import { AlfredActionInterface } from "@/interfaces";
import { generateValidClassNameHandler } from "@/lib/utils";
import { useAlfredChatContext } from "@/providers/alfred/chat";
import { Button } from "@/ui/button";

const getIcon = (iconName?: string) => {
    switch (iconName) {
    case "eye":
        return <Eye className="size-4" />;
    case "message-circle":
        return <Notification className="size-4" />;
    case "check":
        return <CheckMark className="size-4" />;
    case "x":
        return <Cancel className="size-4" />;
    case "play":
        return <Play className="size-4" />;
    case "download":
        return <Download className="size-4" />;
    case "flag":
        return <Alert2 className="size-4" />;
    case "alert-triangle":
        return <Alert2 className="size-4" />;
    case "send":
        return <ArrowTurnForward className="size-4" />;
    default:
        return null;
    }
};

const getButtonVariant = (variant?: AlfredActionInterface["variant"]) => {
    switch (variant) {
    case "outline":
        return {
            state: ButtonStateEnum.DEFAULT,
            variant: "outline" as const,
        };
    case "success":
        return {
            state: ButtonStateEnum.DEFAULT,
            variant: "default" as const,
        };
    case "danger":
        return {
            state: ButtonStateEnum.DANGER,
            variant: "default" as const,
        };
    case "warning":
        return {
            state: ButtonStateEnum.DEFAULT,
            variant: "outline" as const,
        };
    default:
        return {
            state: ButtonStateEnum.DEFAULT,
            variant: "default" as const,
        };
    }
};

const getButtonColorClass = (variant?: AlfredActionInterface["variant"]) => {
    switch (variant) {
    case "success":
        return "!bg-green-600 hover:!bg-green-700";
    case "warning":
        return "!border-amber-500 !text-amber-600 hover:!bg-amber-50";
    default:
        return "";
    }
};

export const AlfredActionsButtons = ({
    actions,
    className,
    onAction,
} : {
    actions: AlfredActionInterface[],
    className?: string,
    onAction?: (actionId: string) => void, // eslint-disable-line
}) => {
    const { sendMessage } = useAlfredChatContext();

    const handleClick = (action: AlfredActionInterface) => {
        if (action.disabled) return;

        // Handle built-in actions
        switch (action.id) {
        case "view_details":
            // Navigation handled by parent via onAction
            break;
        case "ask_alfred":
            sendMessage("Tell me more about this");
            return;
        default:
            break;
        }

        // Delegate to parent handler
        if (onAction) onAction(action.id);
    };

    return (
        <div className={generateValidClassNameHandler(
            "flex flex-wrap gap-2",
            className,
        )}
        >
            {actions.map((action) => {
                const {
                    state,
                    variant,
                } = getButtonVariant(action.variant);
                const colorClass = getButtonColorClass(action.variant);
                const icon = getIcon(action.icon);

                return (
                    <Button
                        className={generateValidClassNameHandler(colorClass)}
                        disabled={action.disabled}
                        key={action.id}
                        size="sm"
                        state={state}
                        variant={variant}
                        onClick={() => handleClick(action)}
                    >
                        {icon}
                        {action.label}
                    </Button>
                );
            })}
        </div>
    );
};
